import { ReactElement, MouseEvent, KeyboardEvent } from 'react';
import { Tooltip } from '@c2fo/liquidity';
import { getContainerProps, getAccessibilityProps } from './components/NavigationItemContainer';
import { NavigationItemContent } from './components/NavigationItemContent';
import { getSubItemBaseClassName } from './components/NavigationSubItemBase';
import { NavigationItem, RenderItemProps, RenderSubItemProps } from './Navigation';
import { animationClassNames } from './utils';

export interface ElementCreators {
  createMainElement: (props: {
    containerProps: Record<string, unknown>;
    accessibilityProps?: Record<string, unknown>;
    onClick: (event: MouseEvent) => void;
    onKeyDown: (event: KeyboardEvent) => void;
    content: ReactElement;
    isDisabled: boolean;
  }) => ReactElement;

  createSubElement: (props: {
    item: NavigationItem;
    className: string;
    isDisabled: boolean;
    onClick: (event: MouseEvent) => void;
    onKeyDown: (event: KeyboardEvent) => void;
  }) => ReactElement;
}

export const createRenderers = (creators: ElementCreators) => {
  const renderItem = ({
    item,
    isActive,
    isDisabled,
    hasSubItems,
    isCollapsed,
    isSubNavOpen,
    onClick,
    onKeyDown,
  }: RenderItemProps) => {
    const isSkinnyNav = isCollapsed || isSubNavOpen;

    const content = (
      <NavigationItemContent
        item={item}
        isActive={isActive}
        hasSubItems={hasSubItems}
        animationClassNames={animationClassNames}
        iconWrapper={isSkinnyNav ? (icon) => <Tooltip.Trigger asChild>{icon}</Tooltip.Trigger> : undefined}
      />
    );

    const containerProps = getContainerProps({ item, isDisabled });
    const accessibilityProps = getAccessibilityProps(isDisabled);

    const navigationItem = creators.createMainElement({
      containerProps,
      accessibilityProps,
      onClick,
      onKeyDown,
      content,
      isDisabled,
    });

    if (isSkinnyNav) {
      return (
        <Tooltip key={item.id} delayDuration={300}>
          {navigationItem}
          <Tooltip.Portal>
            <Tooltip.Content arrow variant="dark" side="right" sideOffset={0}>
              <p className="text-sm font-semibold">{item.label}</p>
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip>
      );
    }

    return navigationItem;
  };

  const renderSubItem = ({ item, isActive, isDisabled, onClick, onKeyDown }: RenderSubItemProps) => {
    const className = getSubItemBaseClassName({
      isActive,
      isDisabled,
      animationClassNames,
    });

    return creators.createSubElement({
      item,
      className,
      isDisabled,
      onClick,
      onKeyDown,
    });
  };

  return { renderItem, renderSubItem };
};
